import React, { useState, useEffect, useRef } from 'react';
import { countries } from '../utils/countries';
import { Phone } from 'lucide-react';

const CountryPhoneInput = ({
  countryCode,
  setCountryCode,
  onCountryCodeChange,
  mobileNumber,
  phone,
  setPhone,
  onMobileNumberChange,
  disabled = false,
  required = true,
  placeholder = "Mobile Number",
  buttonStyle = {},
  inputStyle = {},
  variant = "default",
  hasError = false,
  className = ""
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Normalize prop aliases so both naming conventions work identically
  const effectiveCountryCode = countryCode || '+91';
  const handleCountryCodeChange = onCountryCodeChange || setCountryCode || (() => {});
  const effectiveMobileNumber = mobileNumber !== undefined ? mobileNumber : (phone !== undefined ? phone : '');
  const handleMobileNumberChange = onMobileNumberChange || setPhone || (() => {});

  // Find currently selected country
  const selectedCountry = countries.find(c => c.dial === effectiveCountryCode) || countries[0];

  // Filter countries by search query
  const filteredCountries = countries.filter(c =>
    c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.dial.includes(searchQuery) ||
    c.code.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // Click outside listener to close dropdown
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectCountry = (dialCode) => {
    handleCountryCodeChange(dialCode);
    setIsOpen(false);
    setSearchQuery('');
  };

  if (variant === 'premium') {
    return (
      <div 
        className={`phone-field ${isOpen ? 'is-open' : ''} ${disabled ? 'is-disabled' : ''} ${hasError ? 'apply-input-error' : ''} ${className}`}
        style={{ boxSizing: 'border-box' }}
      >
        <div className="phone-field-icon-wrap" style={{ display: 'flex', alignItems: 'center', paddingLeft: '13px', color: '#8aa198', flexShrink: 0 }}>
          <Phone size={16} className="phone-icon" />
        </div>
         {/* Searchable Dropdown Wrapper */}
        <div 
          ref={dropdownRef} 
          style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'stretch',
            boxSizing: 'border-box'
          }}
        >
          <button
            type="button"
            className="phone-country-code"
            onClick={() => !disabled && setIsOpen(!isOpen)}
            disabled={disabled}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '7px',
              padding: '0 12px',
              border: 'none',
              borderRight: '1px solid #e2e9e5',
              background: '#f8fbf9',
              color: '#1f332c',
              fontSize: '13px',
              fontWeight: 600,
              cursor: disabled ? 'not-allowed' : 'pointer',
              outline: 'none',
              height: '100%',
              boxSizing: 'border-box'
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              <span style={{ fontSize: '1.25rem', lineHeight: 1 }}>{selectedCountry.flag}</span>
              <span style={{ fontWeight: 650, fontSize: '13px' }}>{selectedCountry.dial}</span>
            </span>
            <span style={{ color: '#94a39c', fontSize: '8px', marginLeft: '2px' }}>▼</span>
          </button>

          {/* Dropdown Menu Option List */}
          {isOpen && (
            <div 
              style={{
                position: 'absolute',
                top: 'calc(100% + 4px)',
                left: 0,
                zIndex: 99999,
                width: '280px',
                maxHeight: '260px',
                backgroundColor: '#ffffff',
                border: '1px solid #d7e2dc',
                borderRadius: '10px',
                boxShadow: '0 10px 25px rgba(15, 70, 52, 0.08)',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                boxSizing: 'border-box'
              }}
            >
              {/* Search Box */}
              <div style={{ padding: '8px', borderBottom: '1px solid #f3f4f6', backgroundColor: '#f9fafb' }}>
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search country name or code..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 10px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '0.82rem',
                    outline: 'none',
                    boxSizing: 'border-box',
                    backgroundColor: '#ffffff',
                    color: '#1f2937'
                  }}
                />
              </div>

              {/* List options */}
              <div style={{ overflowY: 'auto', flex: 1, backgroundColor: '#ffffff' }}>
                {filteredCountries.length > 0 ? (
                  filteredCountries.map((c) => (
                    <div
                      key={`${c.code}-${c.dial}`}
                      onClick={() => handleSelectCountry(c.dial)}
                      style={{
                        padding: '8px 12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                        fontSize: '0.82rem',
                        color: '#374151',
                        transition: 'background-color 0.15s ease',
                        backgroundColor: countryCode === c.dial ? '#f3f4f6' : 'transparent'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = countryCode === c.dial ? '#f3f4f6' : 'transparent'}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden' }}>
                        <span style={{ fontSize: '1.2rem', flexShrink: 0 }}>{c.flag}</span>
                        <span style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontWeight: countryCode === c.dial ? 600 : 400
                        }}>
                          {c.name}
                        </span>
                      </div>
                      <span style={{ fontWeight: 600, color: '#6b7280', flexShrink: 0, marginLeft: '8px' }}>
                        {c.dial}
                      </span>
                    </div>
                  ))
                ) : (
                  <div style={{ padding: '12px', textAlign: 'center', fontSize: '0.8rem', color: '#9ca3af', fontStyle: 'italic' }}>
                    No countries found
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Mobile Input Field */}
        <div style={{ flex: 1, display: 'flex', alignItems: 'stretch' }}>
          <input
            type="tel"
            placeholder={placeholder}
            value={effectiveMobileNumber}
            onChange={(e) => handleMobileNumberChange(e.target.value)}
            required={required}
            disabled={disabled}
            style={{
              flex: 1,
              width: '100%',
              margin: 0,
              boxSizing: 'border-box',
              border: 0,
              outline: 0,
              padding: '11px 13px',
              background: 'transparent',
              fontSize: '13px',
              fontWeight: 500,
              color: '#1f332c'
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`country-phone-wrapper ${hasError ? 'apply-input-error' : ''} ${className}`} 
      style={{
        display: 'flex',
        gap: '8px',
        width: '100%',
        alignItems: 'stretch',
        boxSizing: 'border-box'
      }}
    >
       {/* Searchable Dropdown Wrapper */}
      <div 
        ref={dropdownRef} 
        style={{
          position: 'relative',
          width: '95px',
          minWidth: '95px',
          boxSizing: 'border-box'
        }}
      >
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          style={{
            width: '100%',
            height: '42px',
            padding: '0 8px',
            border: '1px solid var(--border-light, #e5e7eb)',
            borderRadius: '6px',
            backgroundColor: disabled ? 'var(--bg-light, #f3f4f6)' : 'var(--bg-surface-tint, #ffffff)',
            color: 'var(--text-body, #1f2937)',
            cursor: disabled ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontSize: '0.88rem',
            textAlign: 'left',
            boxSizing: 'border-box',
            outline: 'none',
            transition: 'border-color 0.15s ease',
            ...buttonStyle
          }}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            <span style={{ fontSize: '1.15rem' }}>{selectedCountry.flag}</span>
            <span style={{ fontWeight: 600 }}>{selectedCountry.dial}</span>
          </span>
          <span style={{ color: '#9ca3af', fontSize: '0.7rem' }}>▼</span>
        </button>

        {/* Dropdown Menu Option List */}
        {isOpen && (
          <div 
            style={{
              position: 'absolute',
              top: 'calc(100% + 4px)',
              left: 0,
              zIndex: 99999,
              width: '280px',
              maxHeight: '260px',
              backgroundColor: '#ffffff',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              boxSizing: 'border-box'
            }}
          >
            {/* Search Box */}
            <div style={{ padding: '8px', borderBottom: '1px solid #f3f4f6', backgroundColor: '#f9fafb' }}>
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search country name or code..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '0.82rem',
                  outline: 'none',
                  boxSizing: 'border-box',
                  backgroundColor: '#ffffff',
                  color: '#1f2937'
                }}
              />
            </div>

            {/* List options */}
            <div style={{ overflowY: 'auto', flex: 1, backgroundColor: '#ffffff' }}>
              {filteredCountries.length > 0 ? (
                filteredCountries.map((c) => (
                  <div
                    key={`${c.code}-${c.dial}`}
                    onClick={() => handleSelectCountry(c.dial)}
                    style={{
                      padding: '8px 12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                      fontSize: '0.82rem',
                      color: '#374151',
                      transition: 'background-color 0.15s ease',
                      backgroundColor: countryCode === c.dial ? '#f3f4f6' : 'transparent'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = countryCode === c.dial ? '#f3f4f6' : 'transparent'}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden' }}>
                      <span style={{ fontSize: '1.2rem', flexShrink: 0 }}>{c.flag}</span>
                      <span style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontWeight: countryCode === c.dial ? 600 : 400
                      }}>
                        {c.name}
                      </span>
                    </div>
                    <span style={{ fontWeight: 600, color: '#6b7280', flexShrink: 0, marginLeft: '8px' }}>
                      {c.dial}
                    </span>
                  </div>
                ))
              ) : (
                <div style={{ padding: '12px', textAlign: 'center', fontSize: '0.8rem', color: '#9ca3af', fontStyle: 'italic' }}>
                  No countries found
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Mobile Input Field */}
      <div style={{ flex: 1 }}>
        <input
          type="tel"
          className="form-input portal-filter-select"
          placeholder={placeholder}
          value={effectiveMobileNumber}
          onChange={(e) => handleMobileNumberChange(e.target.value)}
          required={required}
          disabled={disabled}
          style={{
            width: '100%',
            height: '42px',
            margin: 0,
            boxSizing: 'border-box',
            ...inputStyle
          }}
        />
      </div>
    </div>
  );
};

export default CountryPhoneInput;
